import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://regard-de-nature.fr";

const NaturopatheDigestion = () => {
  useSEO({
    title: "Digestion difficile : solutions naturelles – Regard de Nature",
    description:
      "Améliorer la digestion naturellement grâce à la naturopathie : alimentation, hygiène de vie, plantes. Accompagnement personnalisé par Laura Elie.",
    canonical: `${SITE}/naturopathe-digestion`,
  });

  return (
    <SeoPageLayout
      h1="Améliorer la digestion naturellement grâce à la naturopathie"
      intro="Ventre gonflé en fin de journée, transit qui fait des siennes, lourdeurs après les repas, aliments que vous n'osez plus manger ? Reprendre contact avec sa digestion, c'est souvent retrouver beaucoup d'énergie et de confort au quotidien."
    >
      <SeoSection title="Quand la digestion devient inconfortable">
        <p>
          Je reçois souvent des personnes qui ont fini par s'habituer à un inconfort digestif permanent : ballonnements
          après les repas, lourdeurs, transit ralenti ou accéléré, fatigue post-repas, sensibilités alimentaires
          qui s'accumulent. Ces signes ne sont pas anodins : ils témoignent souvent d'un déséquilibre qu'il est
          possible d'apaiser en revisitant son hygiène de vie globale.
        </p>
        <p>
          La naturopathie ne pose pas de diagnostic médical et ne traite pas de pathologies. En revanche, elle peut
          vous aider à identifier ce qui sollicite votre digestion et à mettre en place des ajustements doux et
          progressifs pour retrouver du confort.
        </p>
      </SeoSection>

      <SeoSection title="Ce que vous pouvez retrouver">
        <SeoList
          items={[
            "Un ventre plus souple, moins gonflé en fin de journée.",
            "Un transit plus régulier, sans avoir à y penser sans arrêt.",
            "Plus d'énergie après les repas, sans coup de barre systématique.",
            "Une relation apaisée à l'alimentation, sans peur de certains aliments.",
            "Une meilleure compréhension de ce qui apaise — et ce qui irrite — votre système digestif.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comprendre votre terrain digestif">
        <p>
          La digestion est un processus complexe qui implique l'estomac, le foie, la vésicule biliaire, le pancréas,
          les intestins, le microbiote… mais aussi le stress, le rythme des repas, la mastication, les émotions.
        </p>
        <p>
          Dans ma pratique, je regarde l'ensemble de ces dimensions avec vous, sans réduire votre situation à un
          seul symptôme. J'observe que chaque personne a un terrain digestif unique : ce qui apaise l'un peut ne
          pas convenir à l'autre.
        </p>
      </SeoSection>

      <SeoSection title="Les axes de travail en consultation">
        <SeoSubSection title="L'alimentation">
          <SeoList
            items={[
              "Identifier les aliments qui vous fatiguent ou vous apaisent",
              "Favoriser une mastication consciente et un rythme apaisé",
              "Soutenir le microbiote (fibres, fermentés, prébiotiques)",
              "Adapter les repas à votre rythme de vie",
            ]}
          />
        </SeoSubSection>
        <SeoSubSection title="L'hygiène de vie">
          <SeoList
            items={[
              "Sommeil et digestion sont étroitement liés",
              "Activité physique douce pour soutenir le transit",
              "Gestion du stress, qui impacte directement le ventre",
              "Hydratation adaptée et rituels du quotidien",
            ]}
          />
        </SeoSubSection>
        <SeoSubSection title="Les plantes et compléments naturels">
          <p>
            Selon votre situation, je peux vous proposer des suggestions ciblées : plantes de la sphère digestive,
            hydrolats, huiles essentielles (avec précaution), bourgeons. Ces conseils restent éducatifs et
            personnalisés.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="L'axe ventre-cerveau">
        <p>
          On parle de plus en plus du lien entre le ventre et le cerveau, parfois appelé le « deuxième cerveau ».
          Le{" "}
          <Link to="/naturopathe-stress" className="text-primary underline hover:text-secondary">stress</Link>, la
          charge mentale, les émotions enfouies : tout cela peut s'exprimer dans le système digestif. C'est
          pourquoi je vous propose un accompagnement qui prend en compte la globalité, et non uniquement l'assiette.
        </p>
      </SeoSection>

      <SeoSection title="Ce qui guide ma pratique">
        <p>
          Pas de régime restrictif, pas de liste interminable d'aliments « interdits ». Je vous propose des
          ajustements progressifs, ancrés dans votre vie réelle — vos contraintes professionnelles, vos goûts, vos
          repas en famille. C'est dans cette progressivité que la digestion se rééduque vraiment.
        </p>
        <p>
          Je consulte à{" "}
          <Link to="/naturopathe-montesquieu-lauragais" className="text-primary underline hover:text-secondary">
            Montesquieu-Lauragais
          </Link>{" "}
          et à{" "}
          <Link to="/naturopathe-villefranche-de-lauragais" className="text-primary underline hover:text-secondary">
            Villefranche-de-Lauragais
          </Link>
          .
        </p>
      </SeoSection>

      <SeoSection title="Quand consulter un médecin">
        <p className="italic text-foreground/70">
          Si vous présentez des douleurs intenses, des saignements, une perte de poids inexpliquée ou tout symptôme
          inhabituel, consultez en priorité votre médecin traitant ou un gastro-entérologue. La naturopathie
          n'établit pas de diagnostic et n'a pas vocation à remplacer un suivi médical.
        </p>
      </SeoSection>

      {/* CTA fin de page */}
      <section className="mt-10 sm:mt-12 p-6 sm:p-8 bg-gradient-to-br from-primary/5 to-secondary/5 border border-border rounded-lg text-center">
        <h2 className="text-xl sm:text-2xl font-cinzel font-bold text-primary mb-3">
          Retrouver un vrai confort digestif au quotidien
        </h2>
        <div className="w-12 h-px bg-secondary mx-auto mb-4" />
        <p className="text-base text-foreground/80 font-montserrat mb-5 max-w-2xl mx-auto">
          Prendre rendez-vous, c'est commencer à comprendre ce qui se joue dans votre ventre — pour avancer pas à
          pas vers plus de confort.
        </p>
        <Link to="/#accompagnements">
          <Button size="lg" className="bg-gradient-primary text-primary-foreground hover:opacity-90 h-12 px-6 font-montserrat">
            <Calendar className="w-5 h-5 mr-2" />
            Faire le point sur ma digestion
          </Button>
        </Link>
      </section>
    </SeoPageLayout>
  );
};

export default NaturopatheDigestion;
